import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SEO from './SEO';
import Hero from './Hero';
import ImageGallery from './ImageGallery';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { getProductStock } from '../services/stockService';
import { submitOrder, updateStock } from '../services/orderService';

const ProductPage = ({ product }) => {
    const { t, language } = useLanguage();
    const { theme } = useTheme();
    const [selectedSize, setSelectedSize] = useState(null);
    const [stock, setStock] = useState({});
    const [loadingStock, setLoadingStock] = useState(true);
    const [orderForm, setOrderForm] = useState({
        email: '',
        phone: '',
        address: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
    const formRef = useRef();

    // Get translated product data if available, fallback to default
    const translatedProduct = product.translations && product.translations[language]
        ? { ...product, ...product.translations[language] }
        : product;

    // Fetch stock on component mount
    useEffect(() => {
        async function fetchStock() {
            setLoadingStock(true);
            // Always use the original product title for stock lookup
            const productStock = await getProductStock(product.title);
            setStock(productStock);
            setLoadingStock(false);
        }

        if (product) {
            fetchStock();
        }
    }, [product]);

    const handleFormChange = (e) => {
        setOrderForm({
            ...orderForm,
            [e.target.name]: e.target.value
        });
    };

    const handleOrderSubmit = async (e) => {
        e.preventDefault();

        if (!selectedSize) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            await submitOrder({
                product: translatedProduct.title,
                size: selectedSize,
                email: orderForm.email,
                phone: orderForm.phone,
                address: orderForm.address
            });

            // Update stock after successful order submission
            await updateStock(product.title, selectedSize);

            setSubmitStatus('success');
            setOrderForm({ email: '', phone: '', address: '' });
            setSelectedSize(null);

            setTimeout(() => setSubmitStatus(null), 5000);
        } catch (error) {
            console.error('Order submission error:', error);
            setSubmitStatus('error');
            setTimeout(() => setSubmitStatus(null), 5000);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!product) return <div className="min-h-screen flex items-center justify-center">Producto no encontrado</div>;

    return (
        <div className={`${theme === 'dark' ? 'bg-dark-bg' : 'bg-off-white'} min-h-screen`}>
            <SEO
                title={translatedProduct.title}
                description={translatedProduct.description}
                image={product.images[0].src}
                canonicalPath={`/product/${product.id}`}
                article={true}
            />
            <Hero
                title={translatedProduct.title.toUpperCase()}
                subtitle={translatedProduct.subtitle}
                heroAsset={product.heroAsset}
                theme={product.theme}
                productId={product.id}
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                    {/* Sticky Gallery */}
                    <div className="lg:sticky lg:top-24">
                        <ImageGallery images={product.images} productId={product.id} />
                    </div>

                    {/* Product Details */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="space-y-12"
                    >
                        <div>
                            <h2 className={`text-4xl md:text-5xl font-bold ${theme === 'dark' ? 'text-white' : 'text-charcoal'} tracking-tight mb-6`}>
                                {translatedProduct.title}
                            </h2>
                            <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-charcoal'} mb-6`}>
                                {translatedProduct.price} €
                            </p>
                            <p className={`text-xl ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} leading-relaxed font-light`}>
                                {translatedProduct.description}
                            </p>
                        </div>

                        <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} pt-8`}>
                            <h3 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-charcoal'} mb-4`}>{t('product.details')}</h3>
                            <ul className={`space-y-3 text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} font-light`}>
                                {translatedProduct.details.map((detail, index) => (
                                    <li key={index}>• {detail}</li>
                                ))}
                            </ul>
                        </div>

                        <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} pt-8`}>
                            <h3 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-charcoal'} mb-4`}>{t('product.acquire')}</h3>

                            {/* Size Selector */}
                            <div className="mb-6">
                                <p className={`text-sm font-bold mb-3 uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {t('product.selectSize')}
                                </p>
                                <div className="grid grid-cols-3 gap-3">
                                    {['S', 'M', 'L'].map(size => {
                                        const sizeStock = stock[size] || 0;
                                        const isOutOfStock = sizeStock === 0;
                                        const isSelected = selectedSize === size;

                                        return (
                                            <button
                                                key={size}
                                                onClick={() => !isOutOfStock && setSelectedSize(size)}
                                                disabled={isOutOfStock}
                                                className={`
                                                    relative py-4 px-6 text-center font-bold text-lg tracking-wider transition-all duration-300
                                                    ${isOutOfStock
                                                        ? 'opacity-40 cursor-not-allowed'
                                                        : 'cursor-pointer hover:scale-105'
                                                    }
                                                    ${isSelected
                                                        ? (theme === 'dark' ? 'bg-white text-charcoal' : 'bg-charcoal text-white')
                                                        : (theme === 'dark' ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                                                    }
                                                `}
                                            >
                                                <span className="block mb-1">{size}</span>
                                                <span className="block text-xs font-normal">
                                                    {loadingStock
                                                        ? '...'
                                                        : isOutOfStock
                                                            ? t('product.soldOut')
                                                            : `${sizeStock} ${t('product.inStock')}`
                                                    }
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-8 font-light`}>
                                {t('product.exclusive')}
                            </p>

                            {/* Order Form */}
                            <form ref={formRef} onSubmit={handleOrderSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="email" className={`block text-sm font-bold mb-2 uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {t('product.orderForm.email')}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={orderForm.email}
                                        onChange={handleFormChange}
                                        required
                                        className={`w-full px-4 py-3 border-b-2 focus:outline-none transition-colors duration-300 ${theme === 'dark'
                                            ? 'bg-transparent border-gray-700 text-white focus:border-white placeholder-gray-500'
                                            : 'bg-transparent border-gray-300 text-charcoal focus:border-charcoal placeholder-gray-400'
                                            }`}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="phone" className={`block text-sm font-bold mb-2 uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {t('product.orderForm.phone')}
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={orderForm.phone}
                                        onChange={handleFormChange}
                                        required
                                        className={`w-full px-4 py-3 border-b-2 focus:outline-none transition-colors duration-300 ${theme === 'dark'
                                            ? 'bg-transparent border-gray-700 text-white focus:border-white placeholder-gray-500'
                                            : 'bg-transparent border-gray-300 text-charcoal focus:border-charcoal placeholder-gray-400'
                                            }`}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="address" className={`block text-sm font-bold mb-2 uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {t('product.orderForm.address')}
                                    </label>
                                    <textarea
                                        id="address"
                                        name="address"
                                        value={orderForm.address}
                                        onChange={handleFormChange}
                                        required
                                        rows="3"
                                        placeholder={t('product.orderForm.addressPlaceholder')}
                                        className={`w-full px-4 py-3 border-b-2 focus:outline-none transition-colors duration-300 ${theme === 'dark'
                                            ? 'bg-transparent border-gray-700 text-white focus:border-white placeholder-gray-500'
                                            : 'bg-transparent border-gray-300 text-charcoal focus:border-charcoal placeholder-gray-400'
                                            }`}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={!selectedSize || isSubmitting}
                                    className={`
                                        w-full text-center text-lg px-8 py-4 transition-all duration-300 tracking-widest uppercase font-bold
                                        ${selectedSize && !isSubmitting
                                            ? (theme === 'dark' ? 'bg-white text-charcoal hover:bg-gray-200' : 'bg-charcoal text-white hover:bg-black')
                                            : 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60'
                                        }
                                    `}
                                >
                                    {isSubmitting
                                        ? t('product.orderForm.submitting')
                                        : selectedSize
                                            ? t('product.orderForm.submit')
                                            : t('product.selectSize')
                                    }
                                </button>

                                {submitStatus === 'success' && (
                                    <p className="text-green-500 text-center font-bold">
                                        {t('product.orderForm.success')}
                                    </p>
                                )}

                                {submitStatus === 'error' && (
                                    <p className="text-red-500 text-center font-bold">
                                        {t('product.orderForm.error')}
                                    </p>
                                )}
                            </form>

                            <p className="text-sm text-gray-400 text-center mt-4 tracking-wide">
                                {t('product.limited')}
                            </p>
                        </div>

                        <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} pt-8`}>
                            <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-charcoal'} mb-4`}>{t('product.sizeGuide')}</h3>
                            <div className="overflow-x-auto">
                                <table className={`w-full text-sm text-left ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <thead className={`text-xs uppercase ${theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                                        <tr>
                                            <th className="px-6 py-3">{t('product.size')}</th>
                                            <th className="px-6 py-3">{t('product.width')}</th>
                                            <th className="px-6 py-3">{t('product.length')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className={`${theme === 'dark' ? 'bg-charcoal border-gray-700' : 'bg-white border-b'}`}>
                                            <td className={`px-6 py-4 font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>S</td>
                                            <td className="px-6 py-4">51.5</td>
                                            <td className="px-6 py-4">71</td>
                                        </tr>
                                        <tr className={`${theme === 'dark' ? 'bg-charcoal border-gray-700' : 'bg-white border-b'}`}>
                                            <td className={`px-6 py-4 font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>M</td>
                                            <td className="px-6 py-4">55.5</td>
                                            <td className="px-6 py-4">75</td>
                                        </tr>
                                        <tr className={`${theme === 'dark' ? 'bg-charcoal border-gray-700' : 'bg-white border-b'}`}>
                                            <td className={`px-6 py-4 font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>L</td>
                                            <td className="px-6 py-4">58.5</td>
                                            <td className="px-6 py-4">77</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default ProductPage;
